import { Link, type LinkProps } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

export type EmptyAction =
  | { label: string; to: LinkProps['to']; search?: LinkProps['search'] }
  | { label: string; href: string }
  | { label: string; onClick: () => void };

export type EmptyStateProps = {
  /** ONE sentence stating the result, naming the cause when there is one. */
  message: ReactNode;
  /** At most one action, and it names its destination. */
  action?: EmptyAction;
  className?: string;
};

const linkClass = 'font-medium text-accent-ink hover:underline';

/** Nothing to show is a result, not an absence. One sentence + one action. */
export function EmptyState({ message, action, className }: EmptyStateProps) {
  let actionNode: ReactNode = null;
  if (action && 'to' in action) {
    actionNode = (
      <Link to={action.to} {...(action.search !== undefined ? { search: action.search } : {})} className={linkClass}>
        {action.label}
      </Link>
    );
  } else if (action && 'href' in action) {
    actionNode = <a href={action.href} className={linkClass}>{action.label}</a>;
  } else if (action) {
    actionNode = (
      <button type="button" onClick={action.onClick} className={cn(linkClass, 'border-0 bg-transparent p-0')}>
        {action.label}
      </button>
    );
  }
  return (
    <div className={cn('flex flex-col items-center gap-2 px-4 py-10 text-center text-sm text-muted', className)}>
      <p className="m-0 max-w-[48ch]">{message}</p>
      {actionNode}
    </div>
  );
}
